import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { confirmationModal } from 'vl-common/src/components/Modals';
import { useTranslation } from 'react-i18next';

interface UseLeaveAppointmentConfirmationProps {
  callStatus: string;
}

export const useLeaveAppointmentConfirmation = ({ callStatus }: UseLeaveAppointmentConfirmationProps) => {
  const router = useRouter();
  const { t } = useTranslation('common');

  const routeChangeState = useRef({
    isConfirmingLeave: false,
    confirmedRoute: null as string | null
  });

  const confirmLeaveAppointment = (): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmationModal({
        title: t('Are you sure you want to leave this appointment?'),
        content: t(
          'Leaving now will temporarily disconnect you from the call until you navigate back to the appointment.'
        ),
        okText: t('Leave'),
        cancelText: t('Cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
        autoFocusButton: 'ok',
        okButtonProps: { type: 'primary' },
        cancelButtonProps: { type: 'default' }
      });
    });
  };

  useEffect(() => {
    const handleRouteChangeStart = async (url: string) => {
      const { isConfirmingLeave, confirmedRoute } = routeChangeState.current;

      if (isConfirmingLeave || confirmedRoute === url) return null;

      const currentActiveTabKey = Object.keys(router.query).find((key) => key.startsWith('activeTab_'));
      const newUrlParams = new URLSearchParams(url.split('?')[1]);
      const newActiveTabKey = [...newUrlParams.keys()].find((key) => key.startsWith('activeTab_'));

      const videoCallIsRunning = callStatus === 'in-progress';
      const audioCallIsRunning = !!sessionStorage.getItem('inCall');

      const anyCallIsRunning = videoCallIsRunning || audioCallIsRunning;

      if (anyCallIsRunning && currentActiveTabKey && newActiveTabKey !== currentActiveTabKey) {
        routeChangeState.current.isConfirmingLeave = true;

        router.replace(router.asPath);

        const confirmed = await confirmLeaveAppointment();

        if (confirmed) {
          routeChangeState.current.confirmedRoute = url;
          router.push(url);
        }

        routeChangeState.current.isConfirmingLeave = false;
        return confirmed;
      }

      return true;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [router, callStatus]);
};
